// ─── Clinical Profile Data ──────────────────────────────────────

// Section definitions
// Wizard + dashboard order. Each row in the visual layout pairs a
// patient-presenting concern with its safety-counterpart so the form
// reads as a coherent intake interview:
//   Demographics                       (who are you)
//   Chief complaints  ·  Allergies     (what brought you in + what we must avoid)
//   Past medical Hx   ·  Surgical Hx   (chronic conditions + past procedures)
//   Current meds      ·  Family Hx     (current treatment + hereditary risk)
//   Social Hx · Review of Systems · Review (lifestyle + symptoms sweep + submit)
export const CP_SECTIONS = [
  { id: "demographics", label: "Demographics", icon: "user", color: "#C8B89A" },
  { id: "chief", label: "Chief Complaints", icon: "clipboard", color: "#E07A5F" },
  { id: "allergies", label: "Allergies", icon: "alertCircle", color: "#E07A5F" },
  { id: "pmh", label: "Past Medical History", icon: "heartPulse", color: "#E07A5F" },
  { id: "surgical", label: "Surgical History", icon: "scissors", color: "#5BAAAA" },
  { id: "medications", label: "Current Medications", icon: "pill", color: "#9B8EC4" },
  { id: "family", label: "Family History", icon: "users", color: "#D4A55A" },
  { id: "social", label: "Social History", icon: "coffee", color: "#D4A55A" },
  { id: "ros", label: "Review of Systems", icon: "lungs", color: "#5B9BD5" },
  { id: "review", label: "Review & Submit", icon: "circleCheck", color: "#6BAA75" },
];

// Allowlist of sections the patient can edit on /my-profile (visible cards).
// Mirrored server-side by saveMyClinicalProfile (see spec 2026-05-06).
export const EDITABLE_BY_PATIENT = Object.freeze(["demographics", "medications", "allergies"]);

// Everything the patient may PERSIST via Clinical Profile - Self - Save. This is
// the API write-gate, which is broader than the editable profile cards: it also
// includes hidden, data-only sections that are never rendered as a profile card.
// `peptideIntake` holds the patient's last peptide-checkout health answers
// (medical + screening) so the checkout can prefill instead of re-asking — see
// peptideIntakePrefill.js. Keep EDITABLE_BY_PATIENT for the UI edit-button gate;
// use this list for the save allowlist.
export const PATIENT_WRITABLE_SECTIONS = Object.freeze([...EDITABLE_BY_PATIENT, "peptideIntake"]);

export function canEditByPatient(sectionId) {
  return EDITABLE_BY_PATIENT.includes(sectionId);
}

// Visual grouping clusters cards into related sets so the page reads as a
// scannable medical-admin layout rather than a long stack. Used by both
// ClinicalProfileDetail (provider view) and MyClinicalProfile (patient view)
// so the two surfaces stay visually aligned. Order is preserved within each
// group; ids must match CP_SECTIONS ids.
export const PROFILE_GROUPS = [
  {
    id: "overview",
    labelKey: "pages.clinicalProfileDetail.groups.overview",
    defaultLabel: "Overview",
    sectionIds: ["demographics", "chief"],
  },
  {
    id: "history",
    labelKey: "pages.clinicalProfileDetail.groups.medicalHistory",
    defaultLabel: "Medical history",
    // Order pairs chief↔allergies, pmh↔surgical, meds↔family when the
    // dashboard renders the group as a 2-column grid, matching the
    // wizard flow above (CP_SECTIONS order).
    sectionIds: ["allergies", "pmh", "surgical", "medications", "family"],
  },
  {
    id: "lifestyle",
    labelKey: "pages.clinicalProfileDetail.groups.lifestyle",
    defaultLabel: "Lifestyle & systems",
    sectionIds: ["social", "ros"],
  },
];

// Medical data constants
// 24 entries — fills the 4-col chip grid as 6 even rows. Keep the count
// divisible by 4 (or update the grid layout) if adding/removing items.
export const COMMON_CONDITIONS = [
  "Hypertension",
  "Type 2 Diabetes",
  "Type 1 Diabetes",
  "Hyperlipidemia",
  "Hypothyroidism",
  "Hyperthyroidism",
  "Asthma",
  "COPD",
  "Coronary Artery Disease",
  "Heart Failure",
  "Atrial Fibrillation",
  "Stroke/TIA",
  "Depression",
  "Anxiety",
  "GERD",
  "Chronic Kidney Disease",
  "Liver Disease",
  "Osteoarthritis",
  "Rheumatoid Arthritis",
  "Osteoporosis",
  "Sleep Apnea",
  "Cancer",
  "Seizure Disorder",
  "Migraine",
];

// Per-medication dose + frequency profiles for the quick-pick editor.
// Keys must match the QUICK_ADD_MEDS strings in MedicationsSection.js.
// - commonDoses: chip-style quick picks shown above the free-text dose input
// - defaultFrequency: auto-selected when the user adds the med
// - allowedFrequencies: subset of FREQUENCY_OPTIONS rendered as chips
//   (omit to show all frequencies)
// Medications that take a generic "1 tablet" / "as directed" dose (vitamins,
// supplements) are listed without commonDoses — the patient just types.
export const MEDICATION_PROFILES = {
  Lisinopril: {
    commonDoses: ["2.5mg", "5mg", "10mg", "20mg", "30mg", "40mg"],
    defaultFrequency: "Once daily",
    allowedFrequencies: ["Once daily", "Twice daily"],
  },
  Levothyroxine: {
    commonDoses: ["25mcg", "50mcg", "75mcg", "88mcg", "100mcg", "112mcg", "125mcg", "150mcg", "175mcg", "200mcg"],
    defaultFrequency: "Once daily",
    allowedFrequencies: ["Once daily"],
  },
  Metformin: {
    commonDoses: ["500mg", "850mg", "1000mg"],
    defaultFrequency: "Twice daily",
    allowedFrequencies: ["Once daily", "Twice daily", "3x daily"],
  },
  Atorvastatin: {
    commonDoses: ["10mg", "20mg", "40mg", "80mg"],
    defaultFrequency: "Once daily",
    allowedFrequencies: ["Once daily"],
  },
  Amlodipine: {
    commonDoses: ["2.5mg", "5mg", "10mg"],
    defaultFrequency: "Once daily",
    allowedFrequencies: ["Once daily"],
  },
  Omeprazole: {
    commonDoses: ["10mg", "20mg", "40mg"],
    defaultFrequency: "Once daily",
    allowedFrequencies: ["Once daily", "Twice daily"],
  },
  Losartan: {
    commonDoses: ["25mg", "50mg", "100mg"],
    defaultFrequency: "Once daily",
    allowedFrequencies: ["Once daily", "Twice daily"],
  },
  Gabapentin: {
    commonDoses: ["100mg", "300mg", "400mg", "600mg", "800mg"],
    defaultFrequency: "3x daily",
    allowedFrequencies: ["Once daily", "Twice daily", "3x daily", "As needed"],
  },
  Sertraline: {
    commonDoses: ["25mg", "50mg", "100mg", "150mg", "200mg"],
    defaultFrequency: "Once daily",
    allowedFrequencies: ["Once daily"],
  },
  Metoprolol: {
    commonDoses: ["25mg", "50mg", "100mg", "200mg"],
    defaultFrequency: "Twice daily",
    allowedFrequencies: ["Once daily", "Twice daily"],
  },
  "Vitamin D": {
    commonDoses: ["1000 IU", "2000 IU", "5000 IU", "10000 IU", "50000 IU"],
    defaultFrequency: "Once daily",
  },
  Multivitamin: { defaultFrequency: "Once daily" },
  Magnesium: {
    commonDoses: ["200mg", "300mg", "400mg", "500mg"],
    defaultFrequency: "Once daily",
  },
  Probiotics: { defaultFrequency: "Once daily" },
  Melatonin: {
    commonDoses: ["1mg", "3mg", "5mg", "10mg"],
    defaultFrequency: "Once daily",
    allowedFrequencies: ["Once daily", "As needed"],
  },
  Ibuprofen: {
    commonDoses: ["200mg", "400mg", "600mg", "800mg"],
    defaultFrequency: "As needed",
    allowedFrequencies: ["Twice daily", "3x daily", "As needed"],
  },
  Acetaminophen: {
    commonDoses: ["325mg", "500mg", "650mg"],
    defaultFrequency: "As needed",
    allowedFrequencies: ["Twice daily", "3x daily", "As needed"],
  },
  Aspirin: {
    commonDoses: ["81mg", "325mg"],
    defaultFrequency: "Once daily",
    allowedFrequencies: ["Once daily", "As needed"],
  },
  "Allergy meds": { defaultFrequency: "Once daily", allowedFrequencies: ["Once daily", "As needed"] },
  "Birth Control": { defaultFrequency: "Once daily", allowedFrequencies: ["Once daily"] },
};

export const COMMON_ALLERGENS = [
  "Penicillin",
  "Sulfa Drugs",
  "NSAIDs",
  "Aspirin",
  "Codeine",
  "Morphine",
  "Latex",
  "Iodine/Contrast Dye",
  "Shellfish",
  "Peanuts",
  "Egg",
  "Soy",
];

// 12 entries — fills the 4-col chip grid as 3 even rows. If you add or
// remove items, keep the count divisible by 4 (or update the grid layout)
// so the last row doesn't render ragged.
export const CHIEF_CONCERNS = [
  "Weight gain",
  "Weight loss",
  "Low energy",
  "Brain fog",
  "Poor sleep",
  "Joint pain",
  "Muscle weakness",
  "Low libido",
  "Hair loss",
  "Skin changes",
  "Mood changes",
  "Digestive issues",
];

export const FAMILY_CONDITIONS = [
  "Heart Disease",
  "Diabetes",
  "Cancer",
  "Stroke",
  "Hypertension",
  "Kidney Disease",
  "Mental Illness",
  "Autoimmune Disease",
  "Thyroid Disease",
  "Blood Disorders",
];

export const ROS_SYSTEMS = [
  {
    // Constitutional: dropped "Malaise" — too vague to be useful as a
    // checkbox (and overlaps Fatigue).
    system: "Constitutional",
    items: ["Fever", "Chills", "Fatigue", "Weight loss", "Weight gain", "Night sweats"],
  },
  {
    system: "Cardiovascular",
    items: ["Chest pain", "Palpitations", "Short of breath with activity", "Edema", "Syncope", "Leg pain when walking"],
  },
  {
    // Respiratory: added "Pain on deep breath" — pleuritic chest pain
    // is a respiratory symptom (pleurisy, PE, pneumonia), not a GI one.
    system: "Respiratory",
    items: ["Cough", "Wheezing", "Shortness of breath", "Pain on deep breath", "Hemoptysis", "Sputum production"],
  },
  {
    // Gastrointestinal: removed "Heartburn" — captured separately
    // (and moving it here was leaking acid-reflux complaints into ROS
    // that should be flagged via medications/PMH instead).
    system: "Gastrointestinal",
    items: ["Nausea", "Vomiting", "Diarrhea", "Constipation", "Abdominal pain", "Blood in stool"],
  },
  {
    system: "Musculoskeletal",
    items: ["Joint pain", "Joint swelling", "Muscle pain", "Back pain", "Stiffness", "Limited range of motion"],
  },
  {
    // Psychiatric sits ABOVE Neurological so the section ordering reads
    // "mind first, then nerves" — mood/affect groups feel like they
    // belong with the lifestyle-adjacent sections that precede them
    // (constitutional / GI / MSK = how you feel; psych = how you feel
    // about it). Neurological then follows as the more clinical/
    // sensorimotor block.
    system: "Psychiatric",
    items: ["Depression", "Anxiety", "Insomnia", "Mood changes"],
  },
  {
    // Neurological: removed "Seizures" — a seizure disorder is a
    // diagnosis (already captured in Past Medical History as "Seizure
    // Disorder"), not a transient review-of-systems symptom. Keeps ROS
    // for symptoms, PMH for diagnoses. "Difficulty concentrating" lives
    // here (with Memory changes) since cognitive symptoms are neuro;
    // psychiatric is reserved for mood/affect disorders.
    system: "Neurological",
    items: ["Headaches", "Dizziness", "Numbness/tingling", "Weakness", "Tremor", "Memory changes", "Difficulty concentrating"],
  },
  {
    system: "Endocrine",
    items: ["Heat intolerance", "Cold intolerance", "Excessive thirst", "Frequent urination", "Hair changes"],
  },
];

// Field configs for AddRow components
export const MED_FIELDS = [
  { key: "name", label: "Medication", placeholder: "e.g. Metformin", flex: 2 },
  { key: "dose", label: "Dose", placeholder: "e.g. 500mg" },
  { key: "frequency", label: "Frequency", placeholder: "e.g. Twice daily" },
];

export const ALLERGY_FIELDS = [
  { key: "allergen", label: "Allergen", placeholder: "e.g. Amoxicillin", flex: 2 },
  { key: "reaction", label: "Reaction", placeholder: "e.g. Hives, anaphylaxis" },
];

export const SURGICAL_FIELDS = [
  { key: "procedure", label: "Procedure", placeholder: "e.g. Appendectomy", flex: 2 },
  { key: "year", label: "Year", placeholder: "e.g. 2020" },
];

// 8 most-common surgeries / procedures patients typically report. Fills the
// 4-col chip grid as 2 even rows. "Other" is rendered separately by the
// section so it always opens the free-text Procedure + Year row.
export const COMMON_SURGERIES = [
  "Appendectomy",
  "Cholecystectomy (gallbladder)",
  "Tonsillectomy",
  "Cesarean section",
  "Hernia repair",
  "Cataract surgery",
  "Knee replacement",
  "Hysterectomy",
];

export const FAMILY_FIELDS = [
  { key: "relation", label: "Relation", placeholder: "e.g. Mother" },
  { key: "condition", label: "Condition", placeholder: "e.g. Diabetes", flex: 2 },
  { key: "notes", label: "Notes", placeholder: "Optional" },
];

// Full relation list used by the Family History "Other" dropdown. Covers
// extended family, step-family, and half-family relations beyond the
// QUICK_RELATIONS chip strip (which only shows the 10 most common ones).
export const FAMILY_RELATION_OPTIONS = [
  "Mother",
  "Father",
  "Sister",
  "Brother",
  "Half-sister",
  "Half-brother",
  "Step-mother",
  "Step-father",
  "Maternal Grandmother",
  "Maternal Grandfather",
  "Paternal Grandmother",
  "Paternal Grandfather",
  "Aunt",
  "Uncle",
  "Cousin",
  "Niece",
  "Nephew",
  "Daughter",
  "Son",
  "Other",
];

// Select options
export const SEX_OPTIONS = ["Male", "Female", "Other", "Prefer not to say"];
export const SEVERITY_OPTIONS = ["Mild", "Moderate", "Severe"];
export const SMOKING_OPTIONS = ["Never", "Former", "Current, occasional", "Current, daily"];
export const ALCOHOL_OPTIONS = ["None", "Occasional (1-2/week)", "Moderate (3-7/week)", "Heavy (7+/week)"];
export const SUBSTANCE_OPTIONS = ["None", "Cannabis", "Other"];

// Quick-pick activity level chips — coarse buckets that map to typical
// exercise patterns. Patients can also add a free-text note via the
// "Tell us more" input below for specifics like "yoga 2x, walk daily."
export const ACTIVITY_OPTIONS = ["Sedentary", "Light (1-2x/wk)", "Moderate (3-4x/wk)", "Active (5+/wk)", "Athlete"];

// Common occupation buckets — captures the most operationally relevant
// dimension (sitting vs. physical) without requiring a job title.
export const OCCUPATION_OPTIONS = [
  "Office / desk",
  "Healthcare",
  "Education",
  "Retail / service",
  "Trades / manual",
  "Driving",
  "Retired",
  "Student",
  "Not working",
  "Other",
];

// Build an empty profile object
export const buildEmptyProfile = () => ({
  demographics: {
    firstName: "",
    lastName: "",
    dob: "",
    sex: "",
    email: "",
    phone: "",
    heightFt: "",
    heightIn: "",
    weight: "",
  },
  chief: {
    primaryComplaint: "",
    concerns: [],
    onset: "",
    severity: "",
  },
  pmh: {
    none: false,
    conditions: [],
    other: "",
  },
  medications: {
    none: false,
    list: [],
  },
  allergies: {
    none: false,
    common: [],
    custom: [],
  },
  surgical: {
    none: false,
    list: [],
  },
  family: {
    none: false,
    list: [],
  },
  social: {
    tobacco: "",
    alcohol: "",
    substances: "",
    exercise: "",
    occupation: "",
  },
  ros: {
    systems: {},
  },
  // Hidden, data-only section (not a profile card). Holds the patient's last
  // peptide-checkout health answers for prefill. null until first answered.
  peptideIntake: null,
});

// BMI calculator
export const computeBMI = (heightFt, heightIn, weight) => {
  const ft = parseFloat(heightFt) || 0;
  const inches = parseFloat(heightIn) || 0;
  const w = parseFloat(weight) || 0;
  const totalInches = ft * 12 + inches;
  if (totalInches <= 0 || w <= 0) return null;
  const bmi = (w / (totalInches * totalInches)) * 703;
  return bmi.toFixed(1);
};

// Section completion checks
export const isSectionComplete = (sectionId, profile) => {
  const d = profile[sectionId];
  if (!d) return false;

  switch (sectionId) {
    case "demographics":
      return !!(d.firstName && d.lastName && d.dob && d.sex && d.email);
    case "chief":
      // Marked done when the patient has either picked at least one concern
      // chip OR set a severity. primaryComplaint is the optional bottom
      // textarea and shouldn't gate completion.
      return !!(d.severity || (Array.isArray(d.concerns) && d.concerns.length > 0));
    case "pmh":
      return d.none || d.conditions.length > 0 || !!d.other;
    case "medications":
      return d.none || d.list.length > 0;
    case "allergies":
      return d.none || d.common.length > 0 || d.custom.length > 0;
    case "surgical":
      return d.none || d.list.length > 0;
    case "family":
      return d.none || d.list.length > 0;
    case "social":
      return !!(d.tobacco && d.alcohol);
    case "ros":
      // Always complete — ROS is optional. Empty selection means the
      // patient has no symptoms to flag, which is itself a valid response.
      return true;
    case "review":
      return false;
    default:
      return false;
  }
};
